import {
  ENotificationChannel,
  IGetNotificationsChannelsResponse,
} from 'multirpc-sdk';

import { RootState } from 'store';
import { createQueryFnWithErrorHandler } from 'store/utils/createQueryFnWithErrorHandler';
import { web3Api } from 'store/queries';
import { selectIsMarketingAccepted } from 'modules/auth/store/selectors';
import { setIsMarketingAccepted } from 'modules/auth/store/authSlice';

import { fetchNotificationsChannels } from './notifications/fetchNotificationsChannels';
import { fetchSlackNotificationsBotDetails } from './notifications/fetchSlackNotificationsBotDetails';
import { editNotificationSettings } from './notifications/editNotificationSettings';

export const {
  endpoints: { checkChangedSignupUserSettingsAndUpdate },
  useLazyCheckChangedSignupUserSettingsAndUpdateQuery,
} = web3Api.injectEndpoints({
  endpoints: build => ({
    checkChangedSignupUserSettingsAndUpdate: build.query<
      IGetNotificationsChannelsResponse[],
      void
    >({
      queryFn: createQueryFnWithErrorHandler({
        queryFn: async (_arg, { dispatch, getState }) => {
          const isMarketingAccepted = selectIsMarketingAccepted(
            getState() as RootState,
          );

          dispatch(setIsMarketingAccepted(undefined));

          const { data } = await dispatch(
            fetchNotificationsChannels.initiate(),
          );

          const slackNotificationsChannel = data?.find(
            notification => notification.channel === ENotificationChannel.SLACK,
          );

          let slackBotData;

          if (slackNotificationsChannel) {
            slackBotData = await dispatch(
              fetchSlackNotificationsBotDetails.initiate(),
            );
          }

          if (isMarketingAccepted === undefined) {
            return { data };
          }

          const emailNotificationsChannel = data?.find(
            notification => notification.channel === ENotificationChannel.EMAIL,
          );
          const shouldUpdateEmailNotifications =
            emailNotificationsChannel?.configs?.marketing !==
            isMarketingAccepted &&
            Boolean(emailNotificationsChannel?.is_active);

          const telegramNotificationsChannel = data?.find(
            notification =>
              notification.channel === ENotificationChannel.TELEGRAM,
          );
          const shouldUpdateTelegramNotifications =
            telegramNotificationsChannel &&
            telegramNotificationsChannel?.configs?.marketing !==
            isMarketingAccepted;

          const shouldUpdateSlackNotifications =
            slackNotificationsChannel &&
            Boolean(slackBotData?.data?.channels.length) &&
            Boolean(slackNotificationsChannel.is_active) &&
            slackNotificationsChannel?.configs?.marketing !==
            isMarketingAccepted;

          if (
            shouldUpdateEmailNotifications ||
            shouldUpdateTelegramNotifications ||
            shouldUpdateSlackNotifications
          ) {
            if (shouldUpdateEmailNotifications) {
              await dispatch(
                editNotificationSettings.initiate({
                  channel: ENotificationChannel.EMAIL,
                  config: {
                    ...emailNotificationsChannel?.configs,
                    marketing: isMarketingAccepted,
                  },
                }),
              );
            }

            if (shouldUpdateTelegramNotifications) {
              await dispatch(
                editNotificationSettings.initiate({
                  channel: ENotificationChannel.TELEGRAM,
                  config: {
                    ...telegramNotificationsChannel?.configs,
                    marketing: isMarketingAccepted,
                  },
                }),
              );
            }

            if (shouldUpdateSlackNotifications) {
              await dispatch(
                editNotificationSettings.initiate({
                  channel: ENotificationChannel.SLACK,
                  config: {
                    ...slackNotificationsChannel?.configs,
                    marketing: isMarketingAccepted,
                  },
                }),
              );
            }

            const { data: updatedData } = await dispatch(
              fetchNotificationsChannels.initiate(),
            );

            return { data: updatedData ?? [] };
          }

          return { data };
        },
        errorHandler: error => {
          return {
            error,
          };
        },
      }),
    }),
  }),
});
